import React, { useCallback } from 'react';
import { Heart, Activity } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  exerciseTypes
}) => {
  // Расчет текущего возраста
  const age = parseInt(formData.age) || 30;
  
  // Расчет текущего пульса в покое
  const restingHR = parseInt(formData.restingHeartRate) || 70;
  
  // Обработчик изменения возраста
  const handleAgeChange = useCallback((e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
  }, [setFormData]);
  
  // Обработчик изменения пульса в покое
  const handleRestingHRChange = useCallback((e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      restingHeartRate: newValue
    }));
  }, [setFormData]);
  
  // Предопределенные возрасты для быстрого выбора
  const predefinedAges = [
    { value: '20', label: '20 лет' },
    { value: '30', label: '30 лет' },
    { value: '40', label: '40 лет' },
    { value: '50', label: '50 лет' },
    { value: '60', label: '60 лет' }
  ];
  
  // Функция расчета
  const calculateTargetHR = () => {
    // Здесь вызывается функция основного расчета
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Heart className="w-5 h-5 mr-2 text-red-500" />
        Быстрые настройки
      </h3>
      
      <div className="space-y-5">
        {/* Возраст */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="ageSlider" className="text-gray-600 dark:text-gray-400">Возраст</label>
            <span className="font-medium text-gray-900 dark:text-white">{age} лет</span>
          </div>
          <input
            type="range"
            id="ageSlider"
            min="15"
            max="90"
            step="1"
            value={age}
            onChange={handleAgeChange}
            aria-label="Возраст"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
          />
          
          {/* Кнопки с предопределенными возрастами */}
          <div className="grid grid-cols-5 gap-1 mt-2">
            {predefinedAges.map(ageObj => (
              <button
                key={ageObj.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, age: ageObj.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.age === ageObj.value 
                    ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {ageObj.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Пульс в покое */}
        {formData.includeRestingHR && (
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label htmlFor="restingHRSlider" className="text-gray-600 dark:text-gray-400">Пульс в покое</label>
              <span className="font-medium text-gray-900 dark:text-white">{restingHR} уд/мин</span>
            </div>
            <input
              type="range"
              id="restingHRSlider"
              min="40"
              max="100"
              step="1"
              value={restingHR}
              onChange={handleRestingHRChange}
              aria-label="Пульс в покое"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
            />
            
            {/* Предопределенные значения пульса в покое */}
            <div className="grid grid-cols-3 gap-1 mt-2">
              {[60, 70, 80].map(hr => (
                <button
                  key={hr}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, restingHeartRate: hr.toString() }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${parseInt(formData.restingHeartRate) === hr 
                      ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  {hr} уд/мин
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Включение/отключение пульса в покое */}
        <div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.includeRestingHR}
              onChange={(e) => setFormData(prev => ({ ...prev, includeRestingHR: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать пульс в покое
            </span>
          </label>
        </div>
        
        {/* Выбор пола */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Пол</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'male' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Мужской
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-pink-100 border-pink-300 text-pink-700 dark:bg-pink-900/30 dark:border-pink-700 dark:text-pink-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Женский
            </button>
          </div>
        </div>
        
        {/* Выбор типа тренировки */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Тип тренировки</div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-1 lg:grid-cols-2">
            {exerciseTypes.slice(0, 4).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, exerciseType: type.id }))}
                className={`p-2 text-xs rounded-lg border transition-colors
                  ${formData.exerciseType === type.id 
                    ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTargetHR}
          icon={<Activity className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать зоны пульса
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;